/**
 * @param {object} item
 */
export function normalizeCategoryLabelMap(item) {
    return {
        id: item.id ?? item.Id,
        dimensionKey: item.dimensionKey ?? item.DimensionKey,
        displayName: item.displayName ?? item.DisplayName,
        mappings: item.mappings ?? item.Mappings ?? {},
        enabled: item.enabled ?? item.Enabled ?? true,
    };
}

/**
 * Flat dimensionKey → code → label lookup for enabled maps only.
 * @param {Array<{ dimensionKey: string, mappings?: Record<string, string>, enabled?: boolean }>} items
 */
export function buildCategoryLabelMapsLookup(items = []) {
    const lookup = {};
    for (const item of items) {
        if (item.enabled === false || !item.dimensionKey) continue;
        lookup[item.dimensionKey] = item.mappings ?? {};
    }
    return lookup;
}

/**
 * @param {{ key: string, type?: string }[]} [columns]
 * @param {object} [encoding]
 */
export function resolveCategoryColumn(columns = [], encoding = {}) {
    const keys = new Set(columns.map(c => c.key));
    if (encoding.category && keys.has(encoding.category)) return encoding.category;
    return ['category', 'label', 'node_key'].find(k => keys.has(k)) ?? null;
}

/**
 * @param {Record<string, string>} [mapping]
 * @param {unknown} raw
 */
export function mapCategoryCode(mapping, raw) {
    if (!mapping || raw == null) return raw;
    const code = String(raw);
    if (Object.prototype.hasOwnProperty.call(mapping, code)) return mapping[code];
    const lower = code.toLowerCase();
    for (const [k, v] of Object.entries(mapping)) {
        if (String(k).toLowerCase() === lower) return v;
    }
    return code;
}

/**
 * @param {object|null} dataset
 * @param {{ dimensionKey?: string|null, labelMapsLookup?: Record<string, Record<string, string>>, encoding?: object }} [options]
 */
export function applyCategoryLabelMaps(dataset, options = {}) {
    if (!dataset?.rows?.length || !options.dimensionKey) return dataset;

    const mapping = options.labelMapsLookup?.[options.dimensionKey];
    if (!mapping || Object.keys(mapping).length === 0) return dataset;

    const categoryCol = resolveCategoryColumn(dataset.columns, options.encoding);
    if (!categoryCol) return dataset;

    const rows = dataset.rows.map(row => {
        const raw = row[categoryCol];
        const mapped = mapCategoryCode(mapping, raw);
        return mapped === raw ? row : { ...row, [categoryCol]: mapped };
    });

    return rows === dataset.rows ? dataset : { ...dataset, rows };
}
